import { Link } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import { useAppDispatch } from 'hooks/useAppStore';
import { acknowledgeTimezoneDetection } from 'features/auth/authSlice';
import { useTimezoneJustDetected, useUserTimezone } from 'hooks/useUserTimezone';
import { ROUTES } from 'constants/routes';
import { zoneShortLabel } from 'shared/lib/timezone';

/**
 * Shown once, right after first login saved a detected timezone. Detection can
 * pick up a travel or VPN zone, so the sadhak is told what we chose rather than
 * finding class times quietly shifted.
 */
export function TimezoneDetectedNotice() {
  const dispatch = useAppDispatch();
  const justDetected = useTimezoneJustDetected();
  const timezone = useUserTimezone();

  if (!justDetected) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-md rounded-xl border border-border bg-surface-secondary p-3 shadow-lg sm:inset-x-auto sm:right-4 sm:bottom-4"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-muted text-accent-strong"
          aria-hidden
        >
          <Clock className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-content">
            Times are shown in {zoneShortLabel(timezone)}
          </p>
          <p className="mt-0.5 text-xs text-content-secondary">
            We detected {timezone} for your classes, assignments and exams.{' '}
            <Link
              to={ROUTES.SETTINGS}
              className="font-medium text-accent-strong underline underline-offset-2 hover:no-underline dark:text-gold-light"
              onClick={() => dispatch(acknowledgeTimezoneDetection())}
            >
              Change it in Settings
            </Link>
            .
          </p>
        </div>
        <button
          type="button"
          onClick={() => dispatch(acknowledgeTimezoneDetection())}
          className="-mr-1 -mt-1 rounded-lg p-1.5 text-content-tertiary transition hover:bg-surface hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label="Dismiss timezone notice"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
